import { useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, Loader2 } from 'lucide-react';
import { getDemoAttribution, websiteOffer } from '../content/websiteOffer';
import styles from '../pages/RemodelingWebsites.module.css';

export function DemoRequestForm({ remodeling = false }: { remodeling?: boolean }) {
  const navigate = useNavigate();
  const location = useLocation();
  const busy = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy.current) return;
    const data = new FormData(event.currentTarget);
    if (data.get('_gotcha')) return;
    const fields = Object.fromEntries(data.entries());
    const qualified = fields.decision_maker === 'yes' && fields.timeline === 'within-30-days';
    const classification = qualified ? 'ready-for-fit-call' : fields.timeline === 'outside-budget' ? 'outside-budget' : 'needs-review';
    busy.current = true;
    setSubmitting(true);
    setError('');
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch('https://formspree.io/f/mzzaewgd', {
        method: 'POST',
        signal: controller.signal,
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...fields,
          _subject: `Homepage Demo Request — ${classification}`,
          offer: '995-five-page-website',
          audience: remodeling ? 'residential-remodeling' : 'business-website',
          classification,
          source_page: location.pathname,
          ...getDemoAttribution(),
          contact_disclosure: 'Contact by email about this demo request only. No marketing subscription.',
          submitted_at: new Date().toISOString(),
        }),
      });
      if (!response.ok) throw new Error('Request failed');
      // Successful submission is distinct from a calendar visit or confirmed appointment.
      navigate('/book-demo', { state: { demoSubmitted: true, qualified } });
    } catch {
      setError('We could not confirm your request. Please try again, or email nate@vernoweb.com with your business name.');
    } finally {
      window.clearTimeout(timeout);
      busy.current = false;
      setSubmitting(false);
    }
  }

  return <form onSubmit={submit} className={styles.form} aria-busy={submitting}>
    <p className={styles.eyebrow}>START WITH A FREE HOMEPAGE DEMO</p>
    <h3>Tell me a little about your business.</h3>
    <p className={styles.formIntro}>We’ll have a 10-minute call first. If the project fits, I’ll create one homepage direction for you to review, free.</p>
    <p className={styles.formPrice}>Full website: <strong>{websiteOffer.price}</strong> for up to five pages. {websiteOffer.recurringCosts}</p>
    <div className={styles.fields}>
      <label htmlFor="demo-name">Your name<input id="demo-name" name="name" autoComplete="name" required maxLength={120} /></label>
      <label htmlFor="demo-business">Business name<input id="demo-business" name="business" autoComplete="organization" required maxLength={160} /></label>
      <label className={styles.fullField} htmlFor="demo-email">Email<input id="demo-email" name="email" type="email" autoComplete="email" required maxLength={254} /></label>
      <label className={styles.fullField} htmlFor="demo-website">Current website <span>(optional)</span><input id="demo-website" name="website" type="text" inputMode="url" autoComplete="url" placeholder="yourbusiness.com" maxLength={300} /></label>
      <label className={styles.fullField} htmlFor="demo-owner">{remodeling ? 'Do you make website decisions for an operating U.S. residential remodeling business?' : 'Do you make website decisions for an operating business?'}
        <select id="demo-owner" name="decision_maker" required defaultValue=""><option value="" disabled>Select an answer</option><option value="yes">Yes, I’m the owner or decision-maker</option><option value="other-business">I run a different type of business</option><option value="no">No / I’m still getting started</option></select>
      </label>
      <label className={styles.fullField} htmlFor="demo-timeline">Are you considering a $995 website build within 30 days?
        <select id="demo-timeline" name="timeline" required defaultValue=""><option value="" disabled>Select an answer</option><option value="within-30-days">Yes, within the next 30 days</option><option value="later">Possibly, but later</option><option value="outside-budget">That’s outside my budget</option></select>
      </label>
    </div>
    <div className={styles.honeypot} aria-hidden="true"><label>Leave this empty<input name="_gotcha" tabIndex={-1} autoComplete="off" /></label></div>
    {error && <p role="alert" className={styles.error}>{error} <a href="mailto:nate@vernoweb.com">Email Nate</a></p>}
    <button className={styles.button} type="submit" disabled={submitting}>{submitting ? <><Loader2 className="animate-spin" size={18} aria-hidden="true" />Sending request…</> : <>Request my free demo <ArrowRight size={18} aria-hidden="true" /></>}</button>
    <p className={styles.finePrint}>By submitting, you agree that Nate at Verno Web may email you about this request. This does not subscribe you to marketing. <Link to="/privacy">Privacy policy</Link>.</p>
  </form>;
}
